import _ from 'lodash';
import moment from 'moment';
import { SetStateAction } from 'react';
import { Dispatch } from 'redux';
import { ALGORITHM_TYPE, DSP, GOAL_TYPES, GOAL_TYPES_TO_STRATEGY_TYPES, GOAL_VALUE_TYPE, RESULTS_LIMIT, STRATEGY_TYPE, FLIGHT_EXTERNAL_TYPE } from 'constantsBase';
import { ADD_STARRED_USER_STRATEGY, REMOVE_STARRED_USER_STRATEGY } from 'containers/Login/constants';
import {
  getDeepLinkForAMZNOR, getDeepLinkForDBMIOByIO, getDeepLinkForDBMLI, getDeepLinkForTTDCampaign,
  getDeepLinkForTTDLI, getDeepLinkForWMTCampaign, getDeepLinkForWMTLI,
} from 'containers/StrategyWizard/utils';
import { BUDGET_OPT_CONFIG, BUDGET_OPTIMIZATION_ALGO_TYPES, GROUP_SETTINGS, TARGETING_PLUS_ALGO_TYPES } from 'containers/StrategyWizard/ConfigurationByStrategyType/BudgetOptimization/constants';
import { getTargetingPlusAlgoTypeId } from 'containers/StrategyWizard/ConfigurationByStrategyType/BudgetOptimization/mapper';
import { getConfigObjByAlgoTypeId } from 'containers/StrategyWizard/ConfigurationByStrategyType/BudgetOptimization/utils';
import { DAG } from 'containers/StrategyWizard/ConfigurationByStrategyType/constants';
import { ExternalTypeOptimizationLevel, OPTIMIZATION_LEVELS } from 'containers/StrategyWizard/steps/AttachFlights/constants';
import { CONVERSION_BASED_GOALS, REVENUE_TYPE_GOAL_TYPES } from 'containers/StrategyWizard/steps/GoalSelection/constants';
import { BudgetSettingDB } from 'containers/StrategyWizard/types';
import { Currency, FlightCandidate, StarredUserStrategy, Strategy } from 'utils/copilotAPI';
import { getDifferenceBetweenDates, TimeUnits } from 'utils/dateTime';
import desnakify from 'utils/desnakify';
import { pluralizer } from 'utils/formattingUtils';
import { getDeepLinkForAPNCampaign, getDeepLinkForAPNIO, getDeepLinkForAPNLI } from 'utils/functionHelpers';
import { Advertiser, Algorithm, EnhancedStrategy, Flight, Member, DateDifference, StrategyGoalsDB, ToastConfig } from 'utils/types';
import { selectNone } from './actions';
import { ControlTestFlight, EditStrategiesFormType, REMOVE_STARRED_STRATEGY_FROM_LIST, StrategiesListFilterType, StrategyStatus } from './constants';

const { DAY, DAYS, MONTH, MONTHS, YEAR, YEARS } = TimeUnits;

export const starredStratsFilterSelected = () => _.includes(window.location.search, 'starred=true');

export const starMultipleStrategies = async (
  user: number,
  strategyIds: Array<number>,
  setStarStrategyLoading: (x: boolean) => void,
  dispatch: Dispatch<SetStateAction<any>>,
) => {
  setStarStrategyLoading(true);
  try {
    const newlyStarredStrategies = await StarredUserStrategy.starMultipleStrategies({ user, strategyIds });
    dispatch({ type: ADD_STARRED_USER_STRATEGY, payload: newlyStarredStrategies.data });
  } catch (e) {
    console.error(e);
  } finally {
    setStarStrategyLoading(false);
  }
};

export const unstarMultipleStrategies = async (
  strategyStarredEntries: Array<{ stratId: number, starredId: number }>,
  setStarStrategyLoading: (x: boolean) => void,
  dispatch: Dispatch<SetStateAction<any>>,
) => {
  setStarStrategyLoading(true);
  try {
    const starredUserStrategyIds = _.map(strategyStarredEntries, 'starredId');
    await StarredUserStrategy.unstarMultipleStrategies({ id: starredUserStrategyIds });
    dispatch({ type: REMOVE_STARRED_USER_STRATEGY, payload: starredUserStrategyIds });
    // update strategy list if on strategy list page with starred filter enabled
    if (starredStratsFilterSelected()) {
      dispatch({ type: REMOVE_STARRED_STRATEGY_FROM_LIST, payload: _.map(strategyStarredEntries, 'stratId') });
      dispatch(selectNone());
    }
  } catch (e) {
    console.error(e);
  } finally {
    setStarStrategyLoading(false);
  }
};

export const orderStratsByFilter = (strategies: Array<EnhancedStrategy>, filter: StrategiesListFilterType) => (
  _.orderBy(strategies, filter.sort, _.toLower(filter.order) as 'asc' | 'desc')
);

export const isAmazonStrategy = (strategy: EnhancedStrategy) => _.isEqual(STRATEGY_TYPE.amznBudgetOptimization.id, strategy.strategyType.id);

const getActiveBudgetOptAlgo = (strategy: EnhancedStrategy) => _.find(strategy.algorithms, (algo) => (algo.active && _.includes(BUDGET_OPTIMIZATION_ALGO_TYPES, algo.algorithmType)));

const activeTargetingPlusAlgoCheck = (algo: Algorithm) => (algo.active && _.includes(TARGETING_PLUS_ALGO_TYPES, algo.algorithmType));

export const getPrimaryGoal = (strategyGoals: StrategyGoalsDB) => _.find(strategyGoals, { active: true, priority: 1 });

export const isDspCrossPlatform = (dspId: number): boolean => dspId === DSP.MULTIPLE.id;

export const initEditSingleStratFormValues = (strategy: EnhancedStrategy): EditStrategiesFormType => {
  const primaryGoal = getPrimaryGoal(strategy.strategyGoals);
  // the goal is treated as a custom goal if there is no matching goal type
  const goal = _.find(GOAL_TYPES, (gT) => _.isEqual(gT.value, _.camelCase(primaryGoal?.type))) ?? GOAL_TYPES.awgCreateYourOwn;
  const pacing = _.get(getActiveBudgetOptAlgo(strategy), `config.${DAG}.${BUDGET_OPT_CONFIG}.daily_parent_budget_inflation_ratio`);
  const viewabilityGoal = _.find(strategy.strategyGoals, { active: true, priority: 2, type: _.snakeCase(GOAL_TYPES.ivrMeasured.value) });
  const intelligentChildObjects = _.some(strategy.algorithms, activeTargetingPlusAlgoCheck);
  return {
    name: strategy.name,
    goal,
    goalTarget: primaryGoal?.target ?? null,
    pacing,
    viewabilityEnabled: !!viewabilityGoal,
    viewabilityTarget: viewabilityGoal ? viewabilityGoal.target : null,
    intelligentChildObjects,
  };
};

export const initEditMultipleStratFormValues = (selectedStrategies: Array<EnhancedStrategy>) => {
  const individualFormVals = _.map(selectedStrategies, (strat) => initEditSingleStratFormValues(strat));
  const firstStratFormVal = _.head(individualFormVals);

  const allStratsHaveSameFieldVal = (field: keyof EditStrategiesFormType) => _.every(individualFormVals, (val) => _.isEqual(firstStratFormVal[field], val[field]));
  const allStratsHaveSameGoalType = allStratsHaveSameFieldVal('goal');

  return {
    goal: allStratsHaveSameGoalType ? firstStratFormVal.goal : null,
    goalTarget: (allStratsHaveSameGoalType && allStratsHaveSameFieldVal('goalTarget')) ? firstStratFormVal.goalTarget : null,
    pacing: allStratsHaveSameFieldVal('pacing') ? firstStratFormVal.pacing : null,
    viewabilityEnabled: allStratsHaveSameFieldVal('viewabilityEnabled') ? firstStratFormVal.viewabilityEnabled : null,
    viewabilityTarget: allStratsHaveSameFieldVal('viewabilityTarget') ? firstStratFormVal.viewabilityTarget : null,
    intelligentChildObjects: allStratsHaveSameFieldVal('intelligentChildObjects') ? firstStratFormVal.intelligentChildObjects : null,
  };
};

export const editExcludedGoalTypes = [
  ...CONVERSION_BASED_GOALS,
  GOAL_TYPES.cyodCostPerSuccessEvent.value,
  GOAL_TYPES.cyodRateOfSuccessEvent.value,
  GOAL_TYPES.netCpc.value,
  GOAL_TYPES.netCpcv.value,
  GOAL_TYPES.netCpm.value,
  GOAL_TYPES.margin.value,
  GOAL_TYPES.awgCreateYourOwn.value,
  GOAL_TYPES.impactOutcome.value,
];

export const getGoalTypeOptions = (strategies: Array<EnhancedStrategy>) => {
  const strategyTypes = _.uniq(_.map(strategies, 'strategyType.id'));
  // exclude conversion based, weighting required, and revenue type enabled goal types
  // also ensure that the goal types can be applied to every strategy selected
  const goalTypeOptions = _(GOAL_TYPES_TO_STRATEGY_TYPES)
    .chain()
    .pickBy((stratIds: Array<number>, gT: string) => (
      !_.includes(editExcludedGoalTypes, gT) && (_.size(_.intersection(stratIds, strategyTypes)) === _.size(strategyTypes))
    ))
    .keys()
    .map((gT) => _.find(GOAL_TYPES, (goal) => _.isEqual(goal.value, gT)))
    .value();

  if (_.size(strategies) !== 1) {
    return goalTypeOptions;
  }

  // include the original goal if only editing one strategy - for display purposes of custom goals
  const primaryGoal = getPrimaryGoal(_.head(strategies).strategyGoals);
  const primaryGoalType = _.find(GOAL_TYPES, (gT) => _.isEqual(gT.value, _.camelCase(primaryGoal.type))) ?? GOAL_TYPES.awgCreateYourOwn;
  return _.uniqBy([...goalTypeOptions, primaryGoalType], 'value');
};

export const getCurrency = async (flight: Flight) => {
  const currencyRes = await Currency.getById(_.toString(flight.currency));
  return currencyRes.data;
};

export const getGoalTargetLabel = async (valueType: string, singleStrategySelected: EnhancedStrategy) => {
  if (valueType === GOAL_VALUE_TYPE.PERCENTAGE) {
    return '%';
  }
  const firstFlight = singleStrategySelected && _.head(_.values(singleStrategySelected.flights));
  if (valueType === GOAL_VALUE_TYPE.CURRENCY && firstFlight) {
    const currency = await getCurrency(firstFlight);
    return currency.code ?? null;
  }
  return null;
};

export const stratsHaveRevTypeOrCustomGoal = (selectedStrategies: Array<EnhancedStrategy>) => _.some(selectedStrategies, (strat) => {
  const primaryGoalType = getPrimaryGoal(strat.strategyGoals)?.type;
  return _.some(strat.algorithms, { active: true, has_custom_revenue_type: true }) || _.includes(_.map(REVENUE_TYPE_GOAL_TYPES, _.snakeCase), primaryGoalType) || _.isNil(primaryGoalType);
});

export const getEditStrategiesReqObj = (
  selectedStrategies: Array<EnhancedStrategy>,
  formValues: EditStrategiesFormType,
  defaultValues: EditStrategiesFormType,
) => {
  const updatedValues: EditStrategiesFormType = _.transform(defaultValues, (res, v, k) => {
    if (!_.isEqual(v, formValues[k])) {
      // ensure that goal target is a number
      res[k] = (_.isEqual(k, 'goalTarget')) ? _.toNumber(formValues[k]) : formValues[k];
    }
  }, {});
  const goalTypeUpdated = !!updatedValues.goal;
  const updatePrimaryGoal = goalTypeUpdated || updatedValues.goalTarget;
  const includeViewabilityInStratGoals = (!_.isNil(updatedValues.viewabilityEnabled) && updatedValues.viewabilityEnabled)
    || (_.isNil(updatedValues.viewabilityEnabled) && defaultValues.viewabilityEnabled);

  const strategyToSettings = _.transform(selectedStrategies, (res, strat) => {
    const prevCampLvlOptAlgo = getActiveBudgetOptAlgo(strat);
    const prevDagConfig = prevCampLvlOptAlgo.config[DAG];
    const prevGroupSettings = prevDagConfig[GROUP_SETTINGS];
    const prevTargetingPlusAlgo = _.find(strat.algorithms, activeTargetingPlusAlgoCheck);
    const targetingPlusAlgoId = getTargetingPlusAlgoTypeId(prevCampLvlOptAlgo.algorithmType);
    const isAmazonStrat = isAmazonStrategy(strat);
    // intelligent child objects are not available for Amazon
    const includeTargetingPlus = !isAmazonStrat && ((prevTargetingPlusAlgo && _.isNil(updatedValues.intelligentChildObjects)) || updatedValues.intelligentChildObjects);
    const primaryGoal = goalTypeUpdated ? updatedValues.goal.value : _.camelCase(getPrimaryGoal(strat.strategyGoals).type);
    const formValuesForAdditionalMapping = {
      strategyGoals: [{ type: primaryGoal }],
      intelligentChildObjects: includeTargetingPlus,
      bidOptimization: includeTargetingPlus,
    };
    res[strat.id] = {
      ...(updatedValues.name && { name: updatedValues.name }),
      config: {
        [prevCampLvlOptAlgo.algorithmType]: {
          id: prevCampLvlOptAlgo.id,
          name: _.get(prevCampLvlOptAlgo, 'name'),
          enabled: true,
          [DAG]: {
            ...(goalTypeUpdated ? _.omit(prevDagConfig, 'cyod_enabled') : prevDagConfig), // remove cyod references if goal has been changed
            [BUDGET_OPT_CONFIG]: {
              ...prevDagConfig[BUDGET_OPT_CONFIG],
              ...(updatedValues.pacing && { daily_parent_budget_inflation_ratio: updatedValues.pacing }),
            },
          },
        },
        ...(includeTargetingPlus && {
          [targetingPlusAlgoId]: {
            ...(prevTargetingPlusAlgo && { id: prevTargetingPlusAlgo.id }),
            enabled: true,
            [DAG]: {
              // include group settings as backend included references
              ...(prevGroupSettings && { [GROUP_SETTINGS]: prevGroupSettings }),
              // bidOptimization is enabled if intelligentChildObjects is enabled
              ...getConfigObjByAlgoTypeId(targetingPlusAlgoId, formValuesForAdditionalMapping),
              has_custom_revenue_type: (updatedValues.goal ? false : prevDagConfig.has_custom_revenue_type),
            },
          },
        }),
      },
      strategyGoals: [
        // only include primary goal if goal type or target has been changed
        ...(updatePrimaryGoal ? [{ type: _.snakeCase(primaryGoal), target: _.toNumber(updatedValues.goalTarget ?? formValues.goalTarget) }] : []),
        // only include viewability goal for non amazon strategies AND if viewability was previously enabled and unedited OR enabled
        ...((!isAmazonStrat && includeViewabilityInStratGoals) ? [{ type: _.snakeCase(GOAL_TYPES.ivrMeasured.value), target: (updatedValues.viewabilityTarget ?? defaultValues.viewabilityTarget) }] : []),
      ],
    };
  }, {});

  return { strategies: selectedStrategies, strategyToSettings };
};

export const saveEditedStrategies = async (
  selectedStrategies: Array<EnhancedStrategy>,
  formValues: EditStrategiesFormType,
  defaultValues: EditStrategiesFormType,
  setUpdating: (x: boolean) => void,
  afterUpdate: () => void,
  showToast: (config: ToastConfig) => void,
) => {
  setUpdating(true);

  try {
    const resp = await Strategy.bulkEditStrategies(getEditStrategiesReqObj(selectedStrategies, formValues, defaultValues));
    const updates = resp.data;
    const successfulUpdates = _.filter(updates, ['success', true]);
    const failedUpdates = _.filter(updates, ['success', false]);

    if (_.size(successfulUpdates)) {
      showToast({
        type: 'success',
        message: `The following strategies have been successfully updated: ${_.join(_.map(successfulUpdates, 'strategy.id'), ', ')}`,
        duration: 4000,
        maxMessageLines: 4,
      });
    }

    if (_.size(failedUpdates)) {
      showToast({
        type: 'error',
        message: `The following strategies were unable to be updated: ${_.join(_.map(failedUpdates, 'strategy.id'), ', ')}`,
        duration: 4000,
        maxMessageLines: 4,
      });
    }
  } catch (e) {
    console.error(e);
  } finally {
    setUpdating(false);
    afterUpdate();
  }
};

export const getDspLinkForFlight = (flight: Flight): string => {
  if (!flight) return '';

  const { externalId, parentExternalId, externalType, member, advertiser } = flight;
  const memberExternalId = _.get(member, 'externalId');
  const advertiserExternalId = _.get(advertiser, 'externalId');

  switch (externalType) {
    case FLIGHT_EXTERNAL_TYPE.apnInsertionOrder.id:
      return getDeepLinkForAPNIO(externalId);
    case FLIGHT_EXTERNAL_TYPE.apnCampaign.id:
      return getDeepLinkForAPNCampaign(externalId);
    case FLIGHT_EXTERNAL_TYPE.apnLineItem.id:
      return getDeepLinkForAPNLI(externalId);

    case FLIGHT_EXTERNAL_TYPE.ttdCampaign.id:
      return getDeepLinkForTTDCampaign(externalId);
    case FLIGHT_EXTERNAL_TYPE.ttdMegagon.id:
      return getDeepLinkForTTDLI(parentExternalId, externalId);

    case FLIGHT_EXTERNAL_TYPE.wmtCampaign.id:
      return getDeepLinkForWMTCampaign(externalId);
    case FLIGHT_EXTERNAL_TYPE.wmtMegagon.id:
      return getDeepLinkForWMTLI(parentExternalId, externalId);

    case FLIGHT_EXTERNAL_TYPE.dbmLineItem.id:
      return getDeepLinkForDBMLI(flight, advertiserExternalId, memberExternalId);
    case FLIGHT_EXTERNAL_TYPE.dbmInsertionOrder.id:
      return getDeepLinkForDBMIOByIO(flight, advertiserExternalId, memberExternalId);

    case FLIGHT_EXTERNAL_TYPE.amznCampaign.id:
      return getDeepLinkForAMZNOR(flight, memberExternalId);

    default: return '';
  }
};

export const openAllDspLinks = (dspFlights: Array<Flight>) => {
  const links = _.flatMap(dspFlights, (flight) => getDspLinkForFlight(flight));

  _.forEach(_.compact(links), (url) => window.open(url, '_blank'));
};

export const getExactDuration = (dateDifference: DateDifference) => {
  const { days, months, years } = dateDifference;
  if (years) {
    return `${years} ${pluralizer(YEAR, YEARS)(years)}`;
  }
  if (months) {
    return `${months} ${pluralizer(MONTH, MONTHS)(months)}`;
  }
  const totalDays = days === 0 ? days + 1 : days;
  return `${totalDays} ${pluralizer(DAY, DAYS)(totalDays)}`;
};

export const getFlightDatesDurationText = (startDate: Date | string, endDate: Date | string) => {
  const today = moment().startOf(DAY);
  const start = moment(startDate).startOf(DAY);
  const end = moment(endDate).endOf(DAY);
  if (today.isBefore(start)) {
    const difference = getDifferenceBetweenDates(today, start);
    const durationText = getExactDuration(difference);
    return `Starts in ${durationText}`;
  }
  if (today.isAfter(end)) {
    const difference = getDifferenceBetweenDates(end, today);
    const durationText = getExactDuration(difference);
    return `Completed ${durationText} ago`;
  }
  const difference = getDifferenceBetweenDates(today, end);
  const durationText = getExactDuration(difference);
  return `Ends in ${durationText}`;
};

export const getCorrectFlightDates = (budgetSettings: Array<BudgetSettingDB>) => {
  const today = moment().startOf(DAY);
  const startsIn = [];
  const endsIn = [];
  const completed = [];

  _.each(budgetSettings, (bs) => {
    const start = moment(bs.start_date).startOf(DAY);
    const end = moment(bs.end_date).endOf(DAY);
    if (today.isBefore(start)) {
      startsIn.push(bs);
    } else if (today.isBetween(start, end)) {
      endsIn.push(bs);
    } else {
      completed.push(bs);
    }
  });

  if (_.size(endsIn)) {
    return _.head(endsIn);
  }
  if (_.size(completed)) {
    return _.head(_.orderBy(completed, (item) => moment(item.end_date), 'desc'));
  }
  return _.head(_.orderBy(startsIn, (item) => moment(item.start_date), 'asc'));
};

export const getTestControlObject = (flight: Flight, controlObject?: {}) => {
  const externalType = _.get(flight, 'externalType');
  const testFlightExtId = _.get(flight, 'externalId');
  const testFlight = _.get(flight, 'id');
  const controlFlightExtId = _.get(controlObject, 'externalId', null);
  return [{ externalType, controlFlightExtId, testFlightExtId, testFlight }];
};

export const getControlFlights = async (
  member: Member | number,
  advertiser: Advertiser | number,
  dspId: number,
  isCrossPlatform: boolean = false,
) => {
  const memberId = _.toNumber(member) || _.get(member, 'id');
  const advertiserId = _.toNumber(advertiser) || _.get(advertiser, 'id', 'ALL');
  const commonParams = {
    limit: RESULTS_LIMIT,
    sort: 'externalId DESC',
  };
  const candidates = await FlightCandidate.get({
    ...commonParams,
    advertiserId,
    memberId,
    isCrossPlatform,
    externalTypeIds: OPTIMIZATION_LEVELS[dspId]?.[ExternalTypeOptimizationLevel.HIGHER_ORDER]?.externalTypeIds,
    includeExpired: true,
  });
  return _.get(candidates, 'data', []);
};

export const groupByDSP = (flights: Array<Flight>) => {
  const values = _.values(flights);
  return _.groupBy(values, 'dsp');
};

export const getFilteredFlights = (searchStr: string, testFlightData: Record<string, Array<Flight>>) => {
  const searchLower = searchStr.toLowerCase();
  const filteredFlights: typeof testFlightData = {};
  _.forEach(testFlightData, (flights, dsp) => {
    const matchingFlights = _.filter(flights, (flight) => _.includes(_.toLower(flight.externalId), searchLower)
      || _.includes(_.toLower(flight.name), searchLower));
    if (!_.isEmpty(matchingFlights)) {
      filteredFlights[dsp] = matchingFlights;
    }
  });
  return filteredFlights;
};

export const getDefaultTestFlightMap = (testFlightData: Record<string, Array<Flight>>) => {
  const defaultControls: any = {};
  _.forEach(_.flatMap(testFlightData), (flight) => {
    const externalId = flight.externalId;
    defaultControls[externalId] = getTestControlObject(flight, undefined);
  });
  return defaultControls;
};

export const getDateStatus = (dateObj: { startDate: string | Date, endDate: string | Date }) => {
  const { startDate, endDate } = dateObj;
  const today = moment().startOf('day');
  const start = moment(startDate).startOf('day');
  const end = moment(endDate).endOf('day');

  if (today.isBefore(start)) return StrategyStatus.scheduled;

  if (today.isAfter(end)) return StrategyStatus.completed;

  return StrategyStatus.live;
};

const getCurrentStatus = (statuses: Array<StrategyStatus>): StrategyStatus => {
  if (_.includes(statuses, StrategyStatus.live)) return StrategyStatus.live;

  if (_.includes(statuses, StrategyStatus.scheduled)) return StrategyStatus.scheduled;

  return StrategyStatus.completed;
};

export const getStrategyStatus = (strategy: EnhancedStrategy): StrategyStatus => {
  const flights = _.values(strategy.flights);

  if (_.every(flights, ['active', false])) {
    return StrategyStatus.inactive;
  }

  if (isDspCrossPlatform(strategy.dspId)) {
    const budgetSettings = _.get(
      _.find(strategy.algorithms, ['algorithmType', ALGORITHM_TYPE.crossPlatformOptimization.id]),
      `config.${DAG}.budget_settings`,
    );

    const budgetSettingsStatuses = _.map(desnakify(budgetSettings), getDateStatus);
    return getCurrentStatus(budgetSettingsStatuses);
  }

  return getDateStatus(_.head(flights));
};

export function hasDuplicateControlFlightExtIds(flightData: Record<string, Array<ControlTestFlight>>): boolean {
  // Flatten all controlFlightExtId values (excluding nulls)
  const controlIds = _(flightData)
    .values()
    .flatten()
    .map('controlFlightExtId')
    .filter((id): id is string => id !== null)
    .value();

  // Check for duplicates
  const duplicates = _(controlIds)
    .countBy()
    .pickBy((count) => count > 1)
    .keys()
    .value();

  return duplicates.length > 0;
}
